export const META_PIXEL_ID = '1825615778877815'

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[]
  push?: unknown
  loaded?: boolean
  version?: string
}

type PixelState = {
  /** True once fbq('init') has run for this page load. */
  initialized: boolean
  /** True once PageView has been tracked for this page load. */
  pageViewSent: boolean
  /** True once ViewContent has been tracked for this page load. */
  viewContentSent: boolean
  /** Stable event id per page load, used by Meta to deduplicate PageView. */
  pageViewEventId: string
}

declare global {
  interface Window {
    fbq?: Fbq
    _fbq?: Fbq
    __gffcsMetaPixel?: PixelState
  }
}

/**
 * The guard lives on `window`, not in module scope.
 *
 * Module-scoped flags are reset whenever this module is evaluated again (hot
 * reloads, a second bundle instance, StrictMode remounts), which is the usual
 * cause of a pixel initializing twice and PageView firing twice with identical
 * data. A window-level guard survives all of those.
 */
function getState(): PixelState | null {
  if (typeof window === 'undefined') return null
  if (!window.__gffcsMetaPixel) {
    window.__gffcsMetaPixel = {
      initialized: false,
      pageViewSent: false,
      viewContentSent: false,
      pageViewEventId: `pv-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 10)}`,
    }
  }
  return window.__gffcsMetaPixel
}

/** Loads the Meta Pixel base code and runs fbq('init') exactly once. */
function ensureBase(pixelId: string): PixelState | null {
  const state = getState()
  if (!state) return null
  if (state.initialized) return state
  state.initialized = true

  if (!window.fbq) {
    const fbq: Fbq = function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args)
      } else {
        fbq.queue?.push(args)
      }
    } as Fbq

    fbq.queue = []
    fbq.loaded = true
    fbq.version = '2.0'
    fbq.push = fbq

    window.fbq = fbq
    window._fbq = fbq

    const script = document.createElement('script')
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    document.head.appendChild(script)
  }

  window.fbq?.('init', pixelId)
  return state
}

/**
 * Loads the base code (if needed) and fires PageView once per page load.
 * PageView carries a stable eventID so any duplicate delivery is deduplicated
 * by Meta instead of being counted twice.
 */
export function initMetaPixel(pixelId: string = META_PIXEL_ID): void {
  const state = ensureBase(pixelId)
  if (!state || state.pageViewSent) return
  state.pageViewSent = true
  window.fbq?.('track', 'PageView', undefined, {
    eventID: state.pageViewEventId,
  })
}

/** Fires ViewContent once per page load. */
export function trackViewContentOnce(
  params: Record<string, unknown> = PRODUCT_EVENT_PARAMS,
): void {
  const state = ensureBase(META_PIXEL_ID)
  if (!state || state.viewContentSent) return
  state.viewContentSent = true
  window.fbq?.('track', 'ViewContent', params)
}

/** Fires a standard Meta Pixel event, initializing the pixel first if needed. */
export function trackMetaEvent(
  event: 'ViewContent' | 'InitiateCheckout',
  params?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return
  ensureBase(META_PIXEL_ID)
  window.fbq?.('track', event, params)
}

export const PRODUCT_EVENT_PARAMS = {
  content_name: 'Girls Flag Football Coach System',
  content_type: 'product',
  content_ids: ['girls-flag-football-coach-system'],
  value: 27,
  currency: 'USD',
}
